import type { LocaleOption } from '@link/share/constants';

/** A locale option before its `disabled` flag has been resolved. */
type LocaleOptionLike = Omit<LocaleOption, 'disabled'> & {
  disabled?: boolean;
};

export type BuiltLocaleOption<T extends LocaleOptionLike> = Omit<
  T,
  'disabled'
> & { disabled: boolean };

/**
 * Order locale options by `supportedLanguageCodes`: supported locales first,
 * in the order the site/product declares them, then the rest disabled in their
 * original order. An empty list means "no restrictions" — every locale stays
 * enabled in its original order.
 *
 * `supportedLanguageCodes` are UPPERCASE (EN/JA/ZH/ZT/KO) while locale option
 * values are lowercase — bridge with toLowerCase.
 */
export const buildLocaleList = <T extends LocaleOptionLike>(
  localeOptions: readonly T[],
  supportedLanguageCodes: readonly string[] = []
): BuiltLocaleOption<T>[] => {
  const order = supportedLanguageCodes.map(code => code.toLowerCase());
  // Unsupported locales share the last rank and sort is stable, so they keep
  // their original relative order.
  const rank = (value: string) => {
    const index = order.indexOf(value);
    return index === -1 ? order.length : index;
  };

  return [...localeOptions]
    .sort((a, b) => rank(a.value) - rank(b.value))
    .map(
      (locale): BuiltLocaleOption<T> => ({
        ...locale,
        disabled: order.length > 0 && !order.includes(locale.value)
      })
    );
};
